import api from './api';
import { contentApi } from './contentApi';

export interface SchemaProperty {
  type: 'string' | 'number' | 'boolean' | 'array' | 'object';
  description?: string;
  enum?: string[];
  minimum?: number;
  maximum?: number;
  pattern?: string;
  items?: SchemaProperty;
  properties?: Record<string, SchemaProperty>;
  required?: string[];
}

export interface JsonSchema {
  type: 'object';
  title?: string;
  description?: string;
  properties: Record<string, SchemaProperty>;
  required?: string[];
}

export interface SchemaItem {
  id: string;
  name: string;
  description?: string;
  content_type: 'schema';
  folder_path: string;
  created_at: string;
  updated_at: string;
  published_version?: number;
  draft_version?: number;
}

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

/**
 * Schema API - Thin wrapper around contentApi for schema-specific operations
 */
export const schemaApi = {
  /**
   * List all schemas
   */
  async list(): Promise<SchemaItem[]> {
    const items = await contentApi.listItems({ contentType: 'schema' });
    return items as SchemaItem[];
  },

  /**
   * Get a schema item (metadata)
   */
  async get(id: string): Promise<SchemaItem> {
    const item = await contentApi.getItem(id);
    return item as SchemaItem;
  },

  /**
   * Get schema content (JSON Schema object)
   */
  async getContent(id: string): Promise<JsonSchema> {
    const content = await contentApi.getContent(id);
    try {
      // Content might be base64 encoded or raw JSON
      let jsonStr = content;
      if (content.startsWith('{')) {
        // Already JSON
        jsonStr = content;
      } else {
        // Try base64 decode
        try {
          jsonStr = atob(content);
        } catch {
          jsonStr = content;
        }
      }
      return JSON.parse(jsonStr);
    } catch (error) {
      throw new Error('Failed to parse schema content as JSON');
    }
  },

  /**
   * Create a new schema
   */
  async create(name: string, schema: JsonSchema, description?: string): Promise<SchemaItem> {
    const schemaJson = JSON.stringify(schema, null, 2);
    // createItem handles base64 encoding internally
    const item = await contentApi.createItem({
      name,
      content_type: 'schema',
      folder_path: '/schemas',
      content: schemaJson,
      description,
      publish: true,
    });
    return item as SchemaItem;
  },

  /**
   * Update a schema (save draft)
   */
  async update(id: string, schema: JsonSchema, commitMessage?: string): Promise<void> {
    const schemaJson = JSON.stringify(schema, null, 2);
    // saveDraft now handles base64 encoding internally
    await contentApi.saveDraft(id, schemaJson, commitMessage);
  },

  /**
   * Publish a schema
   */
  async publish(id: string): Promise<void> {
    await contentApi.publishVersion(id);
  },

  /**
   * Validate a JSON Schema
   */
  async validate(schema: JsonSchema): Promise<ValidationResult> {
    try {
      const response = await api.post('/content/schemas/validate', schema);
      return response.data;
    } catch (error: any) {
      // If endpoint doesn't exist yet, do basic client-side validation
      const errors: string[] = [];

      if (schema.type !== 'object') {
        errors.push("Schema type must be 'object'");
      }

      if (!schema.properties || typeof schema.properties !== 'object') {
        errors.push('Schema must have a properties object');
      }

      return {
        valid: errors.length === 0,
        errors,
      };
    }
  },

  /**
   * Create an empty schema template
   */
  createEmptySchema(): JsonSchema {
    return {
      type: 'object',
      title: 'NewSchema',
      properties: {},
      required: [],
    };
  },

  /**
   * Create a judge schema template (with required fields)
   */
  createJudgeSchemaTemplate(): JsonSchema {
    return {
      type: 'object',
      title: 'JudgeResult',
      description: 'Custom judge output schema',
      properties: {
        overall_score: {
          type: 'number',
          description: 'Overall evaluation score from 0 to 1',
          minimum: 0,
          maximum: 1,
        },
        verdict: {
          type: 'string',
          description: 'Overall verdict',
          enum: ['pass', 'fail', 'partial', 'error'],
        },
        reasoning: {
          type: 'string',
          description: 'Explanation for the verdict',
        },
      },
      required: ['overall_score', 'verdict'],
    };
  },
};

export default schemaApi;
